import { useSetAtom } from 'jotai';
import { useCallback,useState } from 'react';

import type { IInventoryItem } from '@ekhodealer/ekho-common';

import { clearInventorySelectionAtom } from '../../atoms/inventorySelectionAtoms';
import { usePublishAllInventory } from '../../hooks/usePublishAllInventory';
import { usePublishSelectedInventory } from '../../hooks/usePublishSelectedInventory';

interface UsePublishActionsParams {
	selectedIds: Set<string>;
	selectedCount: number;
}

/**
 * Custom hook that provides action handlers for publishing inventory items
 * Handles both publishing selected items (with modal) and publishing all items (toolbar)
 */
export const usePublishActions = ({ selectedIds }: UsePublishActionsParams) => {
	const {
		handlePublish,
		isPublishing: isPublishingAll,
		publishableCount,
	} = usePublishAllInventory();
	const { publishItemsByIds, isPending: isPublishingSelected } = usePublishSelectedInventory();
	const clearSelection = useSetAtom(clearInventorySelectionAtom);

	// Publish modal state (for selected items)
	const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
	const [publishInitiatedCount, setPublishInitiatedCount] = useState(0);

	// Handle opening publish modal
	const handleOpenPublishModal = useCallback(() => {
		setPublishInitiatedCount(0); // Reset for fresh modal
		setIsPublishModalOpen(true);
	}, []);

	// Handle publish confirmation
	const handlePublishConfirm = useCallback(
		async (readyItems: IInventoryItem[]) => {
			// Use provided items directly (already fetched in modal) to avoid duplicate fetches
			const count = await publishItemsByIds(selectedIds, readyItems);
			setPublishInitiatedCount(count);
			clearSelection();
		},
		[publishItemsByIds, selectedIds, clearSelection],
	);

	// Handle publish cancel
	const handlePublishCancel = useCallback(() => {
		setIsPublishModalOpen(false);
	}, []);

	// Handle publishing all items (for toolbar - no modal)
	const handlePublishAll = useCallback(async () => {
		await handlePublish();
	}, [handlePublish]);

	// Determine publish all button text
	const getPublishAllButtonText = useCallback(() => {
		if (publishableCount && publishableCount > 0) {
			return `Publish all (${publishableCount})`;
		}
		return 'Publish all';
	}, [publishableCount]);

	// Determine if publish all button should be disabled
	const isPublishAllDisabled = isPublishingAll || !publishableCount || publishableCount === 0;

	// Combined publishing state (for loading indicators)
	const isPublishing = isPublishingAll || isPublishingSelected;

	return {
		// Modal state and handlers (for selected items)
		isPublishModalOpen,
		setIsPublishModalOpen,
		publishInitiatedCount,
		isPublishingSelected,
		handleOpenPublishModal,
		handlePublishConfirm,
		handlePublishCancel,
		// Toolbar handlers (for publish all)
		handlePublishAll,
		getPublishAllButtonText,
		isPublishAllDisabled,
		isPublishing,
	};
};
